import { collection, doc, getDoc, getDocs, onSnapshot } from 'firebase/firestore';

import { db } from '@/firebase-config';
import type { ProjectMember, Role } from '@/features/auth/types';
import { getEffectiveUserId } from '@/features/auth/current-user-identity';
import { normalizeCommunicationSettings } from '@/features/email/communication-settings';
import {
  composePersonnelProfiles,
  type PersonnelProfileDoc,
} from '@/features/personnel/personnel-privacy';
import { canReadAllSensitivePersonnelFromMember } from '@/features/personnel/personnel-sensitive-access';
import type { CastAssignment, Personnel } from '@/features/personnel/types';
import type { ProjectDataResourceId } from '@/features/sync/project-data-requirements';
import { normalizeProjectCommunicationAudiences } from '@/features/sync/project-communication-normalizers';
import {
  asArray,
  asString,
  isRecord,
  projectDataResourceUpdateMap,
  toMicPackConfig,
  toProjectMeta,
  toShowStructure,
  type ProjectDataRecord,
} from '@/features/sync/project-data-shape';
import { applyProjectMemberPersonnelLinks } from '@/features/sync/project-personnel-links';
import {
  buildProjectDocPath,
  type ProjectScope,
} from '@/features/sync/project-scope';
import type { CloudState } from '@/store/cloud-types';
import { logger } from '@/utils/logger';

import type {
  ProjectCloudSnapshot,
  ProjectDataClient,
  ProjectDataDocumentRead,
  ProjectDataResourceKey,
  ProjectDataSubscriptionListener,
  ProjectDataSubscriptionUpdate,
  ProjectMemberContext,
  ProjectPersonnelRequest,
  ProjectWriteBaseline,
  ProjectWriteBaselineRequest,
} from '../contracts/project-data-client';

const CONTENT_SUBCOLLECTION = 'data';
const PERSONNEL_PUBLIC_COLLECTION = 'personnelPublic';
const PERSONNEL_SENSITIVE_COLLECTION = 'personnelSensitive';
const CAST_ASSIGNMENTS_COLLECTION = 'castAssignments';

const SHARD_STRUCTURE = 'structure';
const SHARD_PERSONNEL = 'personnel';
const SHARD_PROPS = 'props';
const SHARD_SETS = 'sets';
const SHARD_META = 'meta';
const SHARD_SOUND = 'sound';
const SHARD_LIGHTING = 'lighting';
const SHARD_PROMPTBOOK = 'promptbook';
const SHARD_FLY = 'fly';
const SHARD_PROJECTIONS = 'projections';
const RESOURCE_PROJECT_META = 'projectMeta';
const RESOURCE_SHOW_STRUCTURE = 'showStructure';
const RESOURCE_PERSONNEL = 'personnel';
const RESOURCE_PROPS = 'props';
const RESOURCE_SETS = 'sets';
const RESOURCE_SOUND = 'sound';
const RESOURCE_LIGHTING = 'lighting';
const RESOURCE_PROMPT_BOOK = 'promptBook';
const RESOURCE_FLY = 'fly';
const RESOURCE_PROJECTIONS = 'projections';

const FIREBASE_PROJECT_DATA_DOCUMENT_BY_RESOURCE: Partial<Record<ProjectDataResourceKey, string>> = {
  [RESOURCE_PROJECT_META]: SHARD_META,
  [RESOURCE_SHOW_STRUCTURE]: SHARD_STRUCTURE,
  runSheet: SHARD_STRUCTURE,
  [RESOURCE_PERSONNEL]: SHARD_PERSONNEL,
  [RESOURCE_PROPS]: SHARD_PROPS,
  [RESOURCE_SETS]: SHARD_SETS,
  [RESOURCE_SOUND]: SHARD_SOUND,
  [RESOURCE_LIGHTING]: SHARD_LIGHTING,
  [RESOURCE_PROMPT_BOOK]: SHARD_PROMPTBOOK,
  [RESOURCE_FLY]: SHARD_FLY,
  [RESOURCE_PROJECTIONS]: SHARD_PROJECTIONS,
  costumes: 'costumes',
};

type FirestoreRecord = ProjectDataRecord;

type FirestoreDocumentSnapshot = {
  id: string;
  exists: () => boolean;
  data: () => unknown;
  metadata?: { hasPendingWrites?: boolean };
};
type ProjectResourceRead = {
  resourceId: ProjectDataResourceKey;
  documentId: string;
  loaded: boolean;
  snap: FirestoreDocumentSnapshot | null;
  error?: unknown;
};
type ProfileDocsRead = {
  profiles: PersonnelProfileDoc[];
  readable: boolean;
};
type CastAssignmentsRead = {
  assignments: CastAssignment[];
  readable: boolean;
};

type FirestoreCollectionSnapshot = {
  docs: FirestoreDocumentSnapshot[];
  metadata?: { hasPendingWrites?: boolean };
};

const dataOrEmpty = (snap: FirestoreDocumentSnapshot): FirestoreRecord => {
  if (!snap.exists()) {
    return {};
  }

  const data = snap.data();
  return isRecord(data) ? data : {};
};

const hasPendingLocalWrites = (snap: { metadata?: { hasPendingWrites?: boolean } }): boolean => (
  snap.metadata?.hasPendingWrites === true
);

const profileDocData = (snap: FirestoreDocumentSnapshot): PersonnelProfileDoc => ({
  id: snap.id,
  ...(isRecord(snap.data()) ? snap.data() as FirestoreRecord : {}),
} as PersonnelProfileDoc);

const profileDocsFromSnapshot = (snap: FirestoreCollectionSnapshot): PersonnelProfileDoc[] => (
  snap.docs.map(profileDocData)
);

const castAssignmentDocData = (snap: FirestoreDocumentSnapshot): CastAssignment => {
  const data = isRecord(snap.data()) ? snap.data() as Partial<CastAssignment> : {};
  return {
    ...data,
    id: asString(data.id, snap.id),
  } as CastAssignment;
};

const castAssignmentDocsFromSnapshot = (snap: FirestoreCollectionSnapshot): CastAssignment[] => (
  snap.docs.map(castAssignmentDocData)
);

function mergePersonnelProfileSources(
  publicProfiles: PersonnelProfileDoc[],
  sensitiveProfiles: PersonnelProfileDoc[],
): Personnel[] {
  return composePersonnelProfiles(publicProfiles, sensitiveProfiles);
}

const toProjectMember = (
  docSnap: FirestoreDocumentSnapshot,
  fallbackUserId = docSnap.id,
): ProjectMember => {
  const data = isRecord(docSnap.data()) ? docSnap.data() as Partial<ProjectMember> : {};
  return {
    ...data,
    userId: data.userId || fallbackUserId || docSnap.id,
  } as ProjectMember;
};

const toProjectRole = (docSnap: FirestoreDocumentSnapshot): Role => {
  const data = isRecord(docSnap.data()) ? docSnap.data() as Partial<Role> : {};
  return {
    ...data,
    id: data.id || docSnap.id,
  } as Role;
};

function projectDocRef(scope: ProjectScope) {
  return doc(db, buildProjectDocPath(scope));
}

function projectDataDocRef(scope: ProjectScope, documentId: string) {
  return doc(db, buildProjectDocPath(scope), CONTENT_SUBCOLLECTION, documentId);
}

function projectCollectionRef(scope: ProjectScope, collectionName: string) {
  return collection(db, buildProjectDocPath(scope), collectionName);
}

function projectSubcollectionDocRef(scope: ProjectScope, collectionName: string, docId: string) {
  return doc(db, buildProjectDocPath(scope), collectionName, docId);
}

async function getDocRead(
  ref: Parameters<typeof getDoc>[0],
  label: string,
): Promise<{ snap: FirestoreDocumentSnapshot | null; readable: boolean }> {
  try {
    return {
      snap: await getDoc(ref) as FirestoreDocumentSnapshot,
      readable: true,
    };
  } catch (error) {
    logger.warn(`[firebase-project-data-client] Could not read ${label}; continuing with available project data.`, error);
    return {
      snap: null,
      readable: false,
    };
  }
}

function firebaseProjectDataDocumentIdForResource(resourceId: ProjectDataResourceKey): string | null {
  return FIREBASE_PROJECT_DATA_DOCUMENT_BY_RESOURCE[resourceId] ?? null;
}

async function readProjectResource(
  scope: ProjectScope,
  resourceId: ProjectDataResourceKey,
  label: string,
): Promise<ProjectResourceRead> {
  const documentId = firebaseProjectDataDocumentIdForResource(resourceId);
  if (!documentId) {
    logger.warn(`[firebase-project-data-client] No Firebase project data document is mapped for ${label}.`);
    return {
      resourceId,
      documentId: '',
      loaded: false,
      snap: null,
    };
  }

  try {
    return {
      resourceId,
      documentId,
      loaded: true,
      snap: await getDoc(projectDataDocRef(scope, documentId)) as FirestoreDocumentSnapshot,
    };
  } catch (error) {
    logger.warn(`[firebase-project-data-client] Could not read ${label}; continuing with available project data.`, error);
    return {
      resourceId,
      documentId,
      loaded: false,
      snap: null,
      error,
    };
  }
}

async function readRequiredProjectResource(
  scope: ProjectScope,
  resourceId: ProjectDataResourceKey,
  label: string,
): Promise<ProjectResourceRead> {
  const documentId = firebaseProjectDataDocumentIdForResource(resourceId);
  if (!documentId) {
    throw new Error(`No Firebase project data document is mapped for required ${label}`);
  }

  try {
    return {
      resourceId,
      documentId,
      loaded: true,
      snap: await getDoc(projectDataDocRef(scope, documentId)) as FirestoreDocumentSnapshot,
    };
  } catch (error) {
    logger.warn(`[firebase-project-data-client] Could not read required ${label}; aborting project load.`, error);
    throw new Error(`Could not read required ${label}`, { cause: error });
  }
}

function dataFromResourceRead(read: ProjectResourceRead): FirestoreRecord {
  return read.snap ? dataOrEmpty(read.snap) : {};
}

function documentReadFromResourceRead(read: ProjectResourceRead): ProjectDataDocumentRead {
  return {
    resourceId: read.resourceId,
    readable: read.loaded,
    exists: read.snap?.exists() === true,
    data: dataFromResourceRead(read),
  };
}

function assertProjectResourceReadable(read: ProjectResourceRead): void {
  if (!read.loaded) {
    throw new Error(`Could not read ${read.resourceId} resource for project write baseline`, { cause: read.error });
  }
}

function authoritativeResourceIdsFromReads(reads: readonly ProjectResourceRead[]): ProjectDataResourceKey[] {
  return reads
    .filter(read => read.loaded)
    .map(read => read.resourceId);
}

async function getProfileDocsOrEmpty(
  scope: ProjectScope,
  collectionName: string,
  label: string,
): Promise<PersonnelProfileDoc[]> {
  return (await getProfileDocsRead(scope, collectionName, label)).profiles;
}

async function getProfileDocsRead(
  scope: ProjectScope,
  collectionName: string,
  label: string,
): Promise<ProfileDocsRead> {
  try {
    const snap = await getDocs(projectCollectionRef(scope, collectionName));
    return {
      profiles: profileDocsFromSnapshot(snap as FirestoreCollectionSnapshot),
      readable: true,
    };
  } catch (error) {
    logger.warn(`[firebase-project-data-client] Could not read ${label}; continuing with available project data.`, error);
    return {
      profiles: [],
      readable: false,
    };
  }
}

async function getAccessibleSensitivePersonnelProfiles(
  scope: ProjectScope,
  actorMember?: ProjectMember,
): Promise<PersonnelProfileDoc[]> {
  return (await getAccessibleSensitivePersonnelProfilesRead(scope, actorMember)).profiles;
}

async function getAccessibleSensitivePersonnelProfilesRead(
  scope: ProjectScope,
  actorMember?: ProjectMember,
): Promise<ProfileDocsRead> {
  if (canReadAllSensitivePersonnelFromMember(actorMember)) {
    return getProfileDocsRead(scope, PERSONNEL_SENSITIVE_COLLECTION, 'sensitive personnel profiles');
  }

  const personnelId = actorMember?.personnelId;
  if (!personnelId) {
    return {
      profiles: [],
      readable: true,
    };
  }

  const read = await getDocRead(
    projectSubcollectionDocRef(scope, PERSONNEL_SENSITIVE_COLLECTION, personnelId),
    'own sensitive personnel profile',
  );
  return {
    profiles: read.snap?.exists()
      ? [profileDocData(read.snap)]
      : [],
    readable: read.readable,
  };
}

async function getCastAssignmentsRead(scope: ProjectScope): Promise<CastAssignmentsRead> {
  try {
    const snap = await getDocs(projectCollectionRef(scope, CAST_ASSIGNMENTS_COLLECTION));
    return {
      assignments: castAssignmentDocsFromSnapshot(snap as FirestoreCollectionSnapshot),
      readable: true,
    };
  } catch (error) {
    logger.warn('[firebase-project-data-client] Could not read cast assignments; continuing with available project data.', error);
    return {
      assignments: [],
      readable: false,
    };
  }
}

async function getRequiredCastAssignments(scope: ProjectScope): Promise<CastAssignment[]> {
  try {
    const snap = await getDocs(projectCollectionRef(scope, CAST_ASSIGNMENTS_COLLECTION));
    return castAssignmentDocsFromSnapshot(snap as FirestoreCollectionSnapshot);
  } catch (error) {
    logger.warn('[firebase-project-data-client] Could not read cast assignments; aborting project write baseline.', error);
    throw new Error('Could not read cast assignments for project write baseline', { cause: error });
  }
}

export class FirebaseProjectDataClient implements ProjectDataClient {
  async getProjectDataDocuments(
    scope: ProjectScope,
    resourceIds: readonly ProjectDataResourceKey[],
  ): Promise<ProjectDataDocumentRead[]> {
    const reads = await Promise.all(
      resourceIds.map(resourceId => readProjectResource(scope, resourceId, `${resourceId} resource`)),
    );

    return reads.map(documentReadFromResourceRead);
  }

  async getProjectWriteBaseline(
    scope: ProjectScope,
    request: ProjectWriteBaselineRequest,
  ): Promise<ProjectWriteBaseline> {
    const optionalResourceIds = Array.from(new Set(request.optionalResources));
    const [
      rootSnap,
      structureRead,
      personnelRead,
      metaRead,
      publicProfilesSnap,
      membersSnap,
      castAssignments,
      ...optionalReads
    ] = await Promise.all([
      getDoc(projectDocRef(scope)),
      readRequiredProjectResource(scope, RESOURCE_SHOW_STRUCTURE, 'show structure resource'),
      readProjectResource(scope, RESOURCE_PERSONNEL, 'legacy personnel resource'),
      request.requireProjectMeta === true
        ? readRequiredProjectResource(scope, RESOURCE_PROJECT_META, 'project metadata resource')
        : readProjectResource(scope, RESOURCE_PROJECT_META, 'project metadata resource'),
      getDocs(projectCollectionRef(scope, PERSONNEL_PUBLIC_COLLECTION)),
      getDocs(projectCollectionRef(scope, 'members')),
      request.includeCastAssignments === true
        ? getRequiredCastAssignments(scope)
        : Promise.resolve<CastAssignment[]>([]),
      ...optionalResourceIds.map(resourceId => readProjectResource(scope, resourceId, `${resourceId} resource`)),
    ]);
    const rootDocument = rootSnap as FirestoreDocumentSnapshot;
    const publicProfiles = profileDocsFromSnapshot(publicProfilesSnap as FirestoreCollectionSnapshot);
    const members = (membersSnap as FirestoreCollectionSnapshot).docs.map(docSnap => toProjectMember(docSnap));
    const actorMember = members.find(member => member.userId === request.actorUserId);
    const sensitiveProfiles = await getAccessibleSensitivePersonnelProfiles(scope, actorMember);
    const resources: Partial<Record<ProjectDataResourceKey, ProjectDataRecord>> = {};

    for (const read of optionalReads) {
      assertProjectResourceReadable(read);
      resources[read.resourceId] = dataFromResourceRead(read);
    }
    const personnel = { ...dataFromResourceRead(personnelRead) };
    delete personnel.castAssignments;

    return {
      root: dataOrEmpty(rootDocument),
      structure: dataFromResourceRead(structureRead),
      personnel: {
        ...personnel,
        castAssignments,
      },
      meta: dataFromResourceRead(metaRead),
      resources,
      publicProfiles,
      sensitiveProfiles,
      members,
      actorMember,
    };
  }

  async getPersonnel(scope: ProjectScope, request: ProjectPersonnelRequest = {}): Promise<CloudState['personnel']> {
    const [publicProfilesSnap, membersSnap] = await Promise.all([
      getDocs(projectCollectionRef(scope, PERSONNEL_PUBLIC_COLLECTION)),
      getDocs(projectCollectionRef(scope, 'members')),
    ]);
    const publicProfiles = profileDocsFromSnapshot(publicProfilesSnap as FirestoreCollectionSnapshot);
    const projectMembers = (membersSnap as FirestoreCollectionSnapshot).docs.map(docSnap => toProjectMember(docSnap));
    const currentUserId = getEffectiveUserId();
    const actorMember = currentUserId
      ? projectMembers.find(member => member.userId === currentUserId)
      : undefined;
    const sensitiveProfiles = request.sensitiveProfileAccess === 'full'
      ? await getProfileDocsOrEmpty(scope, PERSONNEL_SENSITIVE_COLLECTION, 'sensitive personnel profiles')
      : await getAccessibleSensitivePersonnelProfiles(scope, actorMember);

    return applyProjectMemberPersonnelLinks(
      mergePersonnelProfileSources(publicProfiles, sensitiveProfiles),
      projectMembers,
    );
  }

  async getProject(scope: ProjectScope): Promise<ProjectCloudSnapshot> {
    const [
      rootSnap,
      structureRead,
      propsRead,
      setsRead,
      soundRead,
      lightingRead,
      promptbookRead,
      flyRead,
      projectionsRead,
      metaRead,
      publicProfilesSnap,
      membersSnap,
      castAssignmentsRead,
    ] = await Promise.all([
      getDoc(projectDocRef(scope)),
      readRequiredProjectResource(scope, RESOURCE_SHOW_STRUCTURE, 'show structure resource'),
      readProjectResource(scope, RESOURCE_PROPS, 'props resource'),
      readProjectResource(scope, RESOURCE_SETS, 'sets resource'),
      readProjectResource(scope, RESOURCE_SOUND, 'sound resource'),
      readProjectResource(scope, RESOURCE_LIGHTING, 'lighting resource'),
      readProjectResource(scope, RESOURCE_PROMPT_BOOK, 'prompt book resource'),
      readProjectResource(scope, RESOURCE_FLY, 'fly resource'),
      readProjectResource(scope, RESOURCE_PROJECTIONS, 'projections resource'),
      readProjectResource(scope, RESOURCE_PROJECT_META, 'project metadata resource'),
      getDocs(projectCollectionRef(scope, PERSONNEL_PUBLIC_COLLECTION)),
      getDocs(projectCollectionRef(scope, 'members')),
      getCastAssignmentsRead(scope),
    ]);
    const rootDocument = rootSnap as FirestoreDocumentSnapshot;

    if (!rootDocument.exists()) {
      throw new Error('No such project!');
    }

    const rootData = dataOrEmpty(rootDocument);
    const structure = dataFromResourceRead(structureRead);
    const props = dataFromResourceRead(propsRead);
    const sets = dataFromResourceRead(setsRead);
    const sound = dataFromResourceRead(soundRead);
    const lighting = dataFromResourceRead(lightingRead);
    const promptbook = dataFromResourceRead(promptbookRead);
    const fly = dataFromResourceRead(flyRead);
    const projections = dataFromResourceRead(projectionsRead);
    const meta = dataFromResourceRead(metaRead);
    const authoritativeResourceIds = authoritativeResourceIdsFromReads([
      structureRead,
      propsRead,
      setsRead,
      soundRead,
      lightingRead,
      promptbookRead,
      flyRead,
      projectionsRead,
      metaRead,
    ]);
    const castAssignments = castAssignmentsRead.assignments;
    const publicProfiles = profileDocsFromSnapshot(publicProfilesSnap as FirestoreCollectionSnapshot);
    const projectMembers = (membersSnap as FirestoreCollectionSnapshot).docs.map(docSnap => toProjectMember(docSnap));
    const currentUserId = getEffectiveUserId();
    const actorMember = currentUserId
      ? projectMembers.find(member => member.userId === currentUserId)
      : undefined;
    const sensitiveProfilesRead = await getAccessibleSensitivePersonnelProfilesRead(scope, actorMember);
    const sensitiveProfiles = sensitiveProfilesRead.profiles;
    const personnelReadAuthoritative = castAssignmentsRead.readable && sensitiveProfilesRead.readable;
    if (personnelReadAuthoritative && !authoritativeResourceIds.includes(RESOURCE_PERSONNEL)) {
      const structureIndex = authoritativeResourceIds.indexOf(RESOURCE_SHOW_STRUCTURE);
      authoritativeResourceIds.splice(structureIndex >= 0 ? structureIndex + 1 : 0, 0, RESOURCE_PERSONNEL);
    }
    const projectPersonnel = applyProjectMemberPersonnelLinks(
      mergePersonnelProfileSources(publicProfiles, sensitiveProfiles),
      projectMembers,
    );
    const rootTitle = asString(rootData.title, asString(rootData.showTitle, 'Untitled'));
    const projectMeta = {
      ...toProjectMeta(meta.projectMeta, rootTitle),
      title: rootTitle,
    };

    return {
      id: scope.projectId,
      orgId: scope.orgId,
      ownerId: asString(rootData.ownerId),
      projectMeta,
      assets: asArray<CloudState['assets'][number]>(meta.assets),
      rows: asArray<CloudState['rows'][number]>(structure.rows),
      showStructure: toShowStructure(structure.showStructure),
      personnel: projectPersonnel,
      trackingColumns: [],
      castAssignments,
      props: asArray<CloudState['props'][number]>(props.props),
      sets: asArray<CloudState['sets'][number]>(sets.sets),
      soundClips: asArray<CloudState['soundClips'][number]>(sound.soundClips),
      soundCues: asArray<CloudState['soundCues'][number]>(sound.soundCues),
      micAssignments: asArray<CloudState['micAssignments'][number]>(sound.micAssignments),
      micSwaps: asArray<CloudState['micSwaps'][number]>(sound.micSwaps),
      micPackConfig: toMicPackConfig(sound.micPackConfig),
      lightingCues: asArray<CloudState['lightingCues'][number]>(lighting.lightingCues),
      followSpotCues: asArray<CloudState['followSpotCues'][number]>(lighting.followSpotCues),
      followSpots: asArray<CloudState['followSpots'][number]>(lighting.followSpots),
      cueAnchors: asArray<CloudState['cueAnchors'][number]>(promptbook.cueAnchors),
      customCues: asArray<CloudState['customCues'][number]>(promptbook.customCues),
      flyCues: asArray<CloudState['flyCues'][number]>(fly.flyCues),
      projectionCues: asArray<CloudState['projectionCues'][number]>(projections.projectionCues),
      isBeta: rootData.isBeta === true,
      scriptTextAccess: rootData.scriptTextAccess === true,
      communicationAudiences: normalizeProjectCommunicationAudiences(rootData.communicationAudiences),
      communicationSettings: normalizeCommunicationSettings(rootData.communicationSettings),
      authoritativeResourceIds,
    };
  }

  subscribeProject(
    scope: ProjectScope,
    request: { resources: readonly ProjectDataResourceKey[] },
    listener: ProjectDataSubscriptionListener,
  ): () => void {
    const unsubs: (() => void)[] = [];
    let publicProfiles: PersonnelProfileDoc[] = [];
    let sensitiveProfiles: PersonnelProfileDoc[] = [];
    let castAssignments: CastAssignment[] = [];
    let castAssignmentsSettled = false;
    let castAssignmentsReadable = false;
    let publicProfilesSettled = false;
    let publicProfilesReadable = false;
    let sensitiveProfilesUnsub: (() => void) | null = null;
    let sensitiveSubscriptionKey = '';
    let sensitiveSubscriptionSettled = false;
    let sensitiveSubscriptionReadable = false;
    let pendingActorMemberUpdate: ProjectMember | null | undefined;

    const emitPersonnelUpdate = () => {
      const personnelSourcesSettled = publicProfilesSettled && sensitiveSubscriptionSettled && castAssignmentsSettled;
      const update: ProjectDataSubscriptionUpdate = {
        personnel: mergePersonnelProfileSources(publicProfiles, sensitiveProfiles),
        castAssignments,
      };
      listener({
        scope,
        resourceId: RESOURCE_PERSONNEL,
        personnelSourcesSettled,
        personnelReadAuthoritative: personnelSourcesSettled
          && publicProfilesReadable
          && sensitiveSubscriptionReadable
          && castAssignmentsReadable,
        update,
      });
    };

    const emitActorMemberUpdate = (actorMember: ProjectMember | null) => {
      listener({
        scope,
        update: { _member: actorMember },
      });
    };

    const emitPersonnelUpdateThenPendingActorMember = () => {
      emitPersonnelUpdate();
      if (pendingActorMemberUpdate !== undefined) {
        emitActorMemberUpdate(pendingActorMemberUpdate);
        pendingActorMemberUpdate = undefined;
      }
    };

    const clearSensitiveProfilesSubscription = () => {
      if (sensitiveProfilesUnsub) {
        sensitiveProfilesUnsub();
        sensitiveProfilesUnsub = null;
      }
      sensitiveSubscriptionKey = '';
      sensitiveSubscriptionSettled = false;
      sensitiveSubscriptionReadable = false;
    };

    const subscribeToAccessibleSensitiveProfiles = (
      actorMember: ProjectMember | null,
      options: { publishMemberWhenReady?: boolean } = {},
    ) => {
      const nextActorMember = actorMember ?? undefined;
      const nextSubscriptionKey = canReadAllSensitivePersonnelFromMember(nextActorMember)
        ? 'all'
        : nextActorMember?.personnelId
          ? `own:${nextActorMember.personnelId}`
          : 'none';

      if (options.publishMemberWhenReady) {
        pendingActorMemberUpdate = actorMember;
      }

      if (nextSubscriptionKey === sensitiveSubscriptionKey) {
        if (options.publishMemberWhenReady && (sensitiveSubscriptionSettled || nextSubscriptionKey === 'none')) {
          emitActorMemberUpdate(actorMember);
          pendingActorMemberUpdate = undefined;
        }
        return;
      }

      clearSensitiveProfilesSubscription();
      sensitiveSubscriptionKey = nextSubscriptionKey;
      sensitiveSubscriptionSettled = nextSubscriptionKey === 'none';
      sensitiveSubscriptionReadable = nextSubscriptionKey === 'none';
      sensitiveProfiles = [];
      if (!sensitiveSubscriptionSettled) {
        emitPersonnelUpdate();
      }

      if (nextSubscriptionKey === 'none') {
        emitPersonnelUpdateThenPendingActorMember();
        return;
      }

      if (nextSubscriptionKey === 'all') {
        sensitiveProfilesUnsub = onSnapshot(
          projectCollectionRef(scope, PERSONNEL_SENSITIVE_COLLECTION),
          snapshot => {
            if (hasPendingLocalWrites(snapshot as FirestoreCollectionSnapshot)) {
              return;
            }
            sensitiveProfiles = profileDocsFromSnapshot(snapshot as FirestoreCollectionSnapshot);
            sensitiveSubscriptionSettled = true;
            sensitiveSubscriptionReadable = true;
            emitPersonnelUpdateThenPendingActorMember();
          },
          error => {
            logger.warn('[firebase-project-data-client] Sensitive personnel profile subscription unavailable for this member.', error);
            sensitiveProfiles = [];
            sensitiveSubscriptionSettled = true;
            sensitiveSubscriptionReadable = false;
            emitPersonnelUpdateThenPendingActorMember();
          },
        );
        return;
      }

      const personnelId = nextActorMember?.personnelId;
      if (!personnelId) {
        emitPersonnelUpdateThenPendingActorMember();
        return;
      }

      sensitiveProfilesUnsub = onSnapshot(
        projectSubcollectionDocRef(scope, PERSONNEL_SENSITIVE_COLLECTION, personnelId),
        docSnap => {
          const snapshot = docSnap as FirestoreDocumentSnapshot;
          if (hasPendingLocalWrites(snapshot)) {
            return;
          }
          sensitiveProfiles = snapshot.exists() ? [profileDocData(snapshot)] : [];
          sensitiveSubscriptionSettled = true;
          sensitiveSubscriptionReadable = true;
          emitPersonnelUpdateThenPendingActorMember();
        },
        error => {
          logger.warn('[firebase-project-data-client] Own sensitive personnel profile subscription unavailable for this member.', error);
          sensitiveProfiles = [];
          sensitiveSubscriptionSettled = true;
          sensitiveSubscriptionReadable = false;
          emitPersonnelUpdateThenPendingActorMember();
        },
      );
    };

    for (const resourceId of request.resources) {
      if (resourceId === RESOURCE_PERSONNEL) {
        const unsubCastAssignments = onSnapshot(
          projectCollectionRef(scope, CAST_ASSIGNMENTS_COLLECTION),
          snapshot => {
            if (hasPendingLocalWrites(snapshot)) {
              return;
            }

            castAssignments = castAssignmentDocsFromSnapshot(snapshot as FirestoreCollectionSnapshot);
            castAssignmentsSettled = true;
            castAssignmentsReadable = true;
            emitPersonnelUpdate();
          },
          error => {
            logger.warn('[firebase-project-data-client] Cast assignments subscription unavailable.', error);
            castAssignments = [];
            castAssignmentsSettled = true;
            castAssignmentsReadable = false;
            emitPersonnelUpdate();
          },
        );
        unsubs.push(unsubCastAssignments);

        const unsubPublicProfiles = onSnapshot(
          projectCollectionRef(scope, PERSONNEL_PUBLIC_COLLECTION),
          snapshot => {
            if (hasPendingLocalWrites(snapshot as FirestoreCollectionSnapshot)) {
              return;
            }
            publicProfiles = profileDocsFromSnapshot(snapshot as FirestoreCollectionSnapshot);
            publicProfilesSettled = true;
            publicProfilesReadable = true;
            emitPersonnelUpdate();
          },
          error => {
            logger.warn('[firebase-project-data-client] Public personnel profile subscription unavailable.', error);
            publicProfilesSettled = true;
            publicProfilesReadable = false;
            emitPersonnelUpdate();
          },
        );
        unsubs.push(unsubPublicProfiles);

        const currentUserId = getEffectiveUserId();
        if (currentUserId) {
          const unsubActorMember = onSnapshot(
            projectSubcollectionDocRef(scope, 'members', currentUserId),
            docSnap => {
              const snapshot = docSnap as FirestoreDocumentSnapshot;
              if (hasPendingLocalWrites(snapshot)) {
                return;
              }
              const actorMember = snapshot.exists() ? toProjectMember(snapshot, currentUserId) : null;
              subscribeToAccessibleSensitiveProfiles(actorMember, { publishMemberWhenReady: true });
            },
            error => {
              logger.warn('[firebase-project-data-client] Actor member subscription unavailable for personnel profile gating.', error);
              subscribeToAccessibleSensitiveProfiles(null, { publishMemberWhenReady: true });
            },
          );
          unsubs.push(unsubActorMember);
        } else {
          subscribeToAccessibleSensitiveProfiles(null);
        }

        continue;
      }

      const mapResource = projectDataResourceUpdateMap[resourceId as ProjectDataResourceId];
      const documentId = firebaseProjectDataDocumentIdForResource(resourceId);
      if (!mapResource || !documentId) {
        continue;
      }

      const unsub = onSnapshot(projectDataDocRef(scope, documentId), docSnap => {
        const snapshot = docSnap as FirestoreDocumentSnapshot;
        if (hasPendingLocalWrites(snapshot)) {
          return;
        }

        if (snapshot.exists()) {
          listener({
            scope,
            resourceId,
            update: mapResource(dataOrEmpty(snapshot)),
          });
        }
      });
      unsubs.push(unsub);
    }

    const unsubRoot = onSnapshot(projectDocRef(scope), docSnap => {
      const snapshot = docSnap as FirestoreDocumentSnapshot;
      if (hasPendingLocalWrites(snapshot)) {
        return;
      }

      if (snapshot.exists()) {
        const data = dataOrEmpty(snapshot);
        listener({
          scope,
          update: {
            projectMeta: {
              title: asString(data.title, asString(data.showTitle)),
            },
            _isBeta: data.isBeta === true,
            _scriptTextAccess: data.scriptTextAccess === true,
            _communicationAudiences: normalizeProjectCommunicationAudiences(data.communicationAudiences),
            _communicationSettings: normalizeCommunicationSettings(data.communicationSettings),
          },
        });
      }
    });
    unsubs.push(unsubRoot);

    return () => {
      clearSensitiveProfilesSubscription();
      for (const unsub of unsubs) {
        unsub();
      }
    };
  }

  async getProjectMemberContext(
    scope: ProjectScope,
    userId: string,
  ): Promise<ProjectMemberContext> {
    const [memberSnap, membersSnap, rolesSnap] = await Promise.all([
      getDoc(projectSubcollectionDocRef(scope, 'members', userId)),
      getDocs(projectCollectionRef(scope, 'members')),
      getDocs(projectCollectionRef(scope, 'roles')),
    ]);
    const memberSnapshot = memberSnap as FirestoreDocumentSnapshot;

    return {
      member: memberSnapshot.exists() ? toProjectMember(memberSnapshot, userId) : null,
      members: (membersSnap as FirestoreCollectionSnapshot).docs.map(docSnap => toProjectMember(docSnap)),
      roles: (rolesSnap as FirestoreCollectionSnapshot).docs.map(toProjectRole),
    };
  }
}
